/*
  assets/nibec-fsb.js
  Mantiene al día la barra de envío gratis que vive FUERA del cart drawer (hoy, la del buy box
  de la PDP).

  El problema: al agregar al carrito, `product-form.js` solo re-renderiza las secciones que
  declara `cart-drawer.js#getSectionsToRender()` — `cart-drawer` y `cart-icon-bubble`. La
  sección de la PDP no está en esa lista, así que su barra quedaba con el valor del último
  page load. Sí publica `PUB_SUB_EVENTS.cartUpdate`, y de ahí nos colgamos.

  El HTML nuevo lo pide a la Section Rendering API (`sections/nibec-fsb.liquid`) en vez de
  calcularlo acá: así el porcentaje, el texto y el formato de moneda los sigue resolviendo
  Liquid, sin duplicar nada en JS ni desincronizarse si mañana cambia el copy.

  Mejora progresiva: si algo falla (sin JS, sin pubsub, error de red) la barra sigue mostrando
  el valor que renderizó el servidor. Nunca queda vacía ni rota.
*/
if (!customElements.get('nibec-fsb')) {
  customElements.define(
    'nibec-fsb',
    class NibecFsb extends HTMLElement {
      connectedCallback() {
        /* La instancia de adentro del drawer no necesita nada: Dawn re-renderiza esa sección
           completa en cada cambio del carrito. */
        if (this.closest('cart-drawer')) return;

        /* ⚠️ `PUB_SUB_EVENTS` se referencia directo, NO por `window`: en `constants.js` se
           declara con `const`, y un `const` en el scope global **no crea propiedad en
           `window`** (a diferencia de `var` o de una function declaration como `subscribe`).
           Con `window.PUB_SUB_EVENTS` el guard daba siempre falso y no se suscribía nunca. */
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.desuscribir = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.sincronizar());
      }

      disconnectedCallback() {
        this.desuscribir?.();
      }

      async sincronizar() {
        try {
          const raiz = window.Shopify?.routes?.root || '/';
          /* Un solo endpoint: todas las instancias muestran lo mismo. Hubo un segundo
             (`nibec-fsb-carrito`) mientras la barra del carrito se escondia al elegir
             regiones o retiro — la Section Rendering API se pide por NOMBRE y no acepta
             parametros, asi que dos comportamientos exigian dos secciones. Con el texto
             calificado ("en Santiago") la barra ya no se esconde nunca y esa seccion se
             borro. */
          const seccion = 'nibec-fsb';
          const respuesta = await fetch(`${raiz}?sections=${seccion}`);
          if (!respuesta.ok) return;

          const secciones = await respuesta.json();
          const nuevoHost = new DOMParser()
            .parseFromString(secciones[seccion], 'text/html')
            .querySelector('nibec-fsb');
          /* Respuesta inesperada: se deja en pantalla lo que ya había, que es válido. */
          if (!nuevoHost) return;

          const nueva = nuevoHost.querySelector('.nibec-fsb');
          const actual = this.querySelector('.nibec-fsb');

          /* La barra puede DESAPARECER si el carrito queda vacío: el servidor devuelve el
             wrapper sin contenido. Antes se salía con `return` y quedaba en pantalla la
             barra vieja con el monto anterior. */
          if (!nueva) {
            actual?.remove();
            return;
          }

          /* ...y puede VOLVER, si el comprador agrega algo de nuevo.
             `importNode` porque el nodo viene de otro documento (el del DOMParser). */
          if (!actual) {
            this.appendChild(document.importNode(nueva, true));
            return;
          }

          /* Se reemplaza el contenido, no el nodo: `.nibec-fsb` es el que lleva la clase
             `--completo` y el que el CSS usa de gancho. Al recrear los hijos, la animación de
             llenado vuelve a correr, que es justo lo que se quiere al cambiar el monto. */
          actual.className = nueva.className;
          actual.innerHTML = nueva.innerHTML;
        } catch (error) {
          /* Silencio a propósito: la barra es informativa y ya muestra un valor válido. */
        }
      }
    }
  );
}
